import type { ApolloQueryResult } from "@apollo/client";
import { useMutation } from "@apollo/client";
import { AppliedFilters, Button, Container, Table } from "@fluidattacks/design";
import { useCallback, useState } from "react";
import type { MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";

import { GroupActionButtons } from "./group-actions";
import { ADD_STAKEHOLDER_MUTATION as ADD_STAKEHOLDER_GROUP_MUTATION } from "./group-actions/queries";
import { handleGrantError } from "./group-actions/utils";
import { OrgActionButtons } from "./org-actions";
import { ADD_STAKEHOLDER_MUTATION as ADD_STAKEHOLDER_ORG_MUTATION } from "./org-actions/queries";
import { getTableColumns } from "./utils";

import type { IStakeholderDataSet } from "../types";
import { useMembersFilters } from "features/members/filters";
import type {
  GetOrganizationStakeholdersQuery,
  GetStakeholdersQuery,
  OrganizationRole,
  StakeholderRole,
} from "gql/graphql";
import { useTable } from "hooks/use-table";
import { msgSuccess } from "utils/notifications";

interface IMembersTableProps {
  organizationId?: string;
  groupName?: string;
  loading: boolean;
  stakeholdersList: IStakeholderDataSet[];
  refetchStakeholders:
    | (() => Promise<ApolloQueryResult<GetOrganizationStakeholdersQuery>>)
    | (() => Promise<ApolloQueryResult<GetStakeholdersQuery>>);
}

const MembersTable = (props: IMembersTableProps): JSX.Element => {
  const {
    stakeholdersList,
    organizationId,
    groupName,
    loading,
    refetchStakeholders,
  } = props;
  const { t } = useTranslation();

  const [grantAccessToOrg] = useMutation(ADD_STAKEHOLDER_ORG_MUTATION, {
    onCompleted: (mtResult): void => {
      if (mtResult.grantStakeholderOrganizationAccess.success) {
        void refetchStakeholders();
        const { email } =
          mtResult.grantStakeholderOrganizationAccess.grantedStakeholder ?? {};
        msgSuccess(
          `${t("searchFindings.tabUsers.success")} ${email}`,
          t("organization.tabs.users.successTitle"),
        );
      }
    },
    onError: (grantError): void => {
      handleGrantError(grantError);
    },
  });

  const [grantAccessToGroup] = useMutation(ADD_STAKEHOLDER_GROUP_MUTATION, {
    onCompleted: (mtResult): void => {
      if (mtResult.grantStakeholderAccess.success) {
        void refetchStakeholders();
        const { email } =
          mtResult.grantStakeholderAccess.grantedStakeholder ?? {};
        msgSuccess(
          `${t("searchFindings.tabUsers.success")} ${email}`,
          t("searchFindings.tabUsers.titleSuccess"),
        );
      }
    },
  });

  const resendEmailHandler = useCallback(
    (stakeholder: IStakeholderDataSet): MouseEventHandler<HTMLButtonElement> =>
      (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.stopPropagation();
        if (organizationId !== undefined) {
          void grantAccessToOrg({
            variables: {
              email: stakeholder.email,
              organizationId,
              role: stakeholder.role.toUpperCase() as OrganizationRole,
            },
          });
        } else if (groupName !== undefined) {
          void grantAccessToGroup({
            variables: {
              email: stakeholder.email,
              groupName,
              role: stakeholder.role.toUpperCase() as StakeholderRole,
            },
          });
        }
      },
    [grantAccessToGroup, grantAccessToOrg, organizationId, groupName],
  );

  const tableColumns = getTableColumns(loading, resendEmailHandler);
  const [filteredData, setFilteredData] = useState<IStakeholderDataSet[]>([]);
  const { Filters, options, removeFilter } = useMembersFilters({
    dataset: stakeholdersList,
    setFilteredDataset: setFilteredData,
    type: groupName === undefined ? "organization" : "group",
  });
  const handleSearch = useCallback(
    (search: string): void => {
      setFilteredData(
        stakeholdersList.filter((stakeholder): boolean =>
          stakeholder.email.toLowerCase().includes(search.toLowerCase()),
        ),
      );
    },
    [stakeholdersList],
  );

  const { table } = useTable({
    columns: tableColumns,
    data: filteredData,
    id: "org-members-table",
    options: {
      actionsOptions: {
        onSearch: handleSearch,
        searchPlaceholder: t("organization.members.searchPlaceholder"),
      },
      filtersMode: "client-side",
      paginationMode: "client-side",
      rowOptions: {
        enableMultiSelection: true,
        getId: (row: IStakeholderDataSet): string => row.email,
      },
    },
    total: filteredData.length,
  });

  const resetOnAction = useCallback((): void => {
    void refetchStakeholders();
    table.resetRowSelection();
  }, [refetchStakeholders, table]);

  const { rowSelection } = table.getState();
  const { data } = table.options.meta ?? {};
  const selectedMembers = Object.keys(rowSelection).map(
    (key): IStakeholderDataSet => data?.get(key) ?? ({} as IStakeholderDataSet),
  );

  return (
    <Table table={table}>
      <Table.Options>
        <Table.Controls>
          <Filters />
          <Container gap={0.5} id={"actions-container"}>
            <Table.Export csvConfig={{ export: true }} data={filteredData}>
              <Button
                icon={"arrow-down-to-bracket"}
                id={"CSVExportBtn"}
                variant={"ghost"}
              >
                {t("group.findings.exportCsv.text")}
              </Button>
            </Table.Export>
            {organizationId !== undefined && (
              <OrgActionButtons
                allMembers={stakeholdersList}
                loading={loading}
                organizationId={organizationId}
                resetOnAction={resetOnAction}
                selectedMembers={selectedMembers}
              />
            )}
            {groupName !== undefined && (
              <GroupActionButtons
                allMembers={stakeholdersList}
                groupName={groupName}
                loading={loading}
                resetOnAction={resetOnAction}
                selectedMembers={selectedMembers}
              />
            )}
          </Container>
        </Table.Controls>
      </Table.Options>
      <Table.Caption>
        <AppliedFilters onClose={removeFilter} options={options} />
      </Table.Caption>
      <Table.Content />
      <Table.Footer />
    </Table>
  );
};

export { MembersTable };
export type { IMembersTableProps };
